import React, { useState } from 'react';
import { View, Text, StyleSheet, Alert, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import '../../i18n';
import { useAuth } from '../../features/auth/useAuth';
import { colors, fonts, spacing } from '../theme';
import { Heading } from '../shared/Typography';
import { TextField } from '../shared/TextField';
import { Button } from '../shared/Button';

export function DeleteAccountScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { deleteAccount } = useAuth();
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = confirmText.trim().toLowerCase() === t('profile.deleteAccount.confirmWord');

  const handleDelete = () => {
    if (!canSubmit) {
      Alert.alert(t('profile.deleteAccount.errorTitle'), t('profile.deleteAccount.confirmWordMismatch'));
      return;
    }

    // Segunda confirmação explícita (nativa) antes de disparar a exclusão
    // real — a ação é irreversível.
    Alert.alert(
      t('profile.deleteAccount.finalAlertTitle'),
      t('profile.deleteAccount.finalAlertMessage'),
      [
        { text: t('profile.deleteAccount.finalAlertCancel'), style: 'cancel' },
        {
          text: t('profile.deleteAccount.finalAlertConfirm'),
          style: 'destructive',
          onPress: async () => {
            setSubmitting(true);
            try {
              await deleteAccount();
              // Sucesso: deleteAccount() já chama signOut(); o auth-gate
              // do root layout (app/_layout.tsx) detecta user === null e
              // redireciona para (auth)/signin sozinho.
            } catch (error) {
              setSubmitting(false);
              Alert.alert(t('profile.deleteAccount.errorTitle'), (error as Error).message);
            }
          },
        },
      ]
    );
  };

  return (
    <View style={styles.container}>
      <Heading>{t('profile.deleteAccount.title')}</Heading>
      <Text style={styles.warning}>{t('profile.deleteAccount.warningIntro')}</Text>
      <View style={styles.list}>
        <Text style={styles.listItem}>{'• ' + t('profile.deleteAccount.itemPhotos')}</Text>
        <Text style={styles.listItem}>{'• ' + t('profile.deleteAccount.itemProjects')}</Text>
        <Text style={styles.listItem}>{'• ' + t('profile.deleteAccount.itemSupplies')}</Text>
        <Text style={styles.listItem}>{'• ' + t('profile.deleteAccount.itemSessions')}</Text>
        <Text style={styles.listItem}>{'• ' + t('profile.deleteAccount.itemAccount')}</Text>
      </View>
      <Text style={styles.instructions}>{t('profile.deleteAccount.confirmInstructions')}</Text>
      <TextField
        placeholder={t('profile.deleteAccount.confirmPlaceholder')}
        value={confirmText}
        onChangeText={setConfirmText}
        autoCapitalize="none"
        editable={!submitting}
      />
      {submitting ? (
        <ActivityIndicator color={colors.sageDeep} />
      ) : (
        <Button title={t('profile.deleteAccount.submit')} variant="danger" onPress={handleDelete} disabled={!canSubmit} />
      )}
      <Button title={t('profile.deleteAccount.cancel')} variant="ghost" onPress={() => router.back()} disabled={submitting} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: spacing.md, justifyContent: 'center', backgroundColor: colors.sand },
  warning: { fontFamily: fonts.body, fontSize: 16, color: colors.ink, marginBottom: spacing.md },
  list: { marginBottom: spacing.md },
  listItem: { fontFamily: fonts.body, fontSize: 15, color: colors.ink, marginBottom: spacing.xs },
  instructions: { fontFamily: fonts.body, fontSize: 14, color: colors.inkSoft, marginBottom: spacing.sm },
});
